registerComponent("modals/abrir-conta", function (component) {
/**
 * ABRIR CONTA — o modal "Baixe nosso aplicativo" (Figma 10680:69101, ADR 0036).
 *
 * · abre a partir de qualquer `[data-abrir-conta]` da página (header, hero,
 *   barra de produtos e a faixa fixa do celular, Investimentos), por delegação
 *   no documento. Os que só abrem o modal são `<button type="button">`; os do
 *   header e do hero seguem `<a>`, porque o destino deles vem do arquivo de
 *   conteúdo — daí o `preventDefault()` continuar necessário;
 * · SÓ NO DESKTOP: em celular e tablet o mesmo clique vai direto à loja, no
 *   destino que o Blade manda em `data-lojas` (o `href` dos selos do
 *   `ds/download-app`). Ver `APARELHO` abaixo;
 * · abre também pelo ENDEREÇO, no fragmento que o Blade manda em `data-ancora`
 *   (`#seja-cliente-asa`, `#become-an-asa-client` em inglês): na carga e a cada
 *   `hashchange`, para um link de fora cair direto no modal. Sair do fragmento
 *   (o Voltar do navegador) fecha, e o fechamento tira o fragmento do endereço
 *   — senão um F5 reabriria o que o visitante acabou de fechar;
 * · é um <dialog>: `showModal()` dá o top layer, o foco contido, o Esc e a
 *   página inerte. A ANIMAÇÃO é por classe, em dois passos: o `display` muda
 *   no showModal e só no quadro seguinte a classe arma a transição (no mesmo
 *   quadro o navegador colapsaria os dois estados);
 * · fecha pelo X, pelo véu e por Esc (o `cancel` é interceptado para a saída
 *   ser animada), devolve o foco a quem abriu e trava a rolagem enquanto está
 *   aberto (`Asa.travarRolagem`);
 * · o campo de SMS do miolo (ADR 0087) é `Asa.formularioSms`, o mesmo do Seja
 *   cliente; ao fechar o modal o formulário volta ao estado inicial (um toast
 *   aberto não sobrevive ao fechamento).
 *
 * @param {HTMLElement} component - o <dialog class="abrir-conta">
 * @param {Object} params
 */

const ABERTO = 'abrir-conta--aberto';
const TRAVA = 'abrir-conta';
// Rede de segurança para o `transitionend` — um pouco mais que a saída (0,3s).
const ESPERA_SAIDA = 600;

/*
 | O CTA vira link para a loja quando o visitante ESTÁ no aparelho que vai
 | instalar o app — celular ou tablet, iOS ou Android. É o aparelho, NÃO a
 | largura: uma janela de desktop estreita continua abrindo o modal, e um tablet
 | deitado em 1200 vai para a loja.
 |
 | Por isso não há `matchMedia` aqui, e a conta é feita uma vez: aparelho não
 | muda no meio da visita.
 |
 | Três leituras, da mais confiável para a última:
 |
 |   1. `userAgentData.mobile` — a resposta que o próprio navegador dá, nos
 |      Chromium. Só vale quando é `true`: no iPad e no Android tablet ela vem
 |      `false`, porque a especificação pergunta "é celular", não "é aparelho".
 |   2. iPadOS 13+ SE DIZ Macintosh no Safari ("Solicitar site para desktop" é o
 |      padrão dele) e nenhuma regex de UA o pega. O que o entrega é o toque:
 |      Mac nenhum tem `maxTouchPoints > 1`.
 |   3. a regex de UA, para todo o resto — a mesma família de nomes que o
 |      `DeviceRedirectController` usa para escolher a loja, mais os tablets.
 */
const APARELHO = (() => {
    const ua = navigator.userAgent || '';
    const dados = navigator.userAgentData;

    if (dados && dados.mobile === true) return true;
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;

    return /Android|iPhone|iPad|iPod|Windows Phone|IEMobile|BlackBerry|BB10|webOS|Opera Mini|Silk|Kindle|PlayBook|Mobile|Tablet/i.test(ua);
})();

const dialog = /** @type {HTMLDialogElement} */ (/** @type {unknown} */ (component));
const painel = component.querySelector('.abrir-conta__painel');
const sms = Asa.formularioSms(component.querySelector('.baixe-app__sms'));

/** O fragmento que abre o modal, sem o `#`. Vazio se o Blade não mandar. */
const ancora = component.dataset.ancora || '';

/** O destino dos selos das lojas. Vazio faz o CTA voltar a abrir o modal. */
const lojas = component.dataset.lojas || '';

/** Quem abriu, para receber o foco de volta. @type {HTMLElement|null} */
let gatilho = null;
let fechando = false;
// Tagueamento (plano v3.1): quando abriu e como vai fechar, para o `modal_close`.
let abertoEm = 0;
let comoFechou = 'x';

const abrir = (origem) => {
    // Sem <dialog> (navegador antigo) quem é `<a>` segue como link; quem é
    // `<button>` não faz nada, que é o mesmo que o `href="#"` fazia — rolar ao
    // topo nunca foi destino.
    if (typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;

    gatilho = origem;
    fechando = false;

    Asa.travarRolagem(TRAVA);
    dialog.showModal();

    abertoEm = Date.now();
    comoFechou = 'x';
    Asa.track('modal_open', origem
        ? { modal_name: 'abrir_conta', origin: origem.dataset.trackOrigin, method: 'click' }
        : { modal_name: 'abrir_conta', origin: 'deep_link', method: 'hash' });

    if (!Asa.movimentoPermitido()) {
        dialog.classList.add(ABERTO);
        return true;
    }

    // Força o layout com o dialog já visível (estado "fechado" pintado) antes
    // de armar a classe — é o que faz a transição existir.
    void painel.offsetWidth;
    requestAnimationFrame(() => dialog.classList.add(ABERTO));

    return true;
};

const fechar = () => {
    if (!dialog.open || fechando) return;

    fechando = true;
    dialog.classList.remove(ABERTO);

    if (!Asa.movimentoPermitido()) {
        dialog.close();
        return;
    }

    let feito = false;

    const concluir = () => {
        if (feito) return;
        feito = true;
        painel.removeEventListener('transitionend', aoTerminar);
        dialog.close();
    };

    const aoTerminar = (event) => {
        if (event.target === painel && event.propertyName === 'opacity') concluir();
    };

    painel.addEventListener('transitionend', aoTerminar);
    setTimeout(concluir, ESPERA_SAIDA);
};

/** O endereço está na âncora do modal? */
const noEndereco = () => ancora !== '' && decodeURIComponent(window.location.hash).slice(1) === ancora;

/*
 | Tira o fragmento do endereço sem mexer na rolagem nem no histórico: o
 | `replaceState` não dispara `hashchange`, logo não reentra aqui.
 */
const limparEndereco = () => {
    if (!noEndereco()) return;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
};

// O `close` é o único ponto de saída — cobre o Esc que o navegador não deixa
// interceptar (o segundo Esc seguido, sem ativação do usuário) e o fecho por
// script.
dialog.addEventListener('close', () => {
    Asa.track('modal_close', { modal_name: 'abrir_conta', method: comoFechou, seconds: Math.round((Date.now() - abertoEm) / 1000) });

    fechando = false;
    dialog.classList.remove(ABERTO);
    Asa.soltarRolagem(TRAVA);
    if (sms) sms.reiniciar();
    limparEndereco();

    // `preventScroll`: a página ficou travada enquanto o modal esteve aberto,
    // o gatilho continua onde estava — e sem isso o Chrome rolava ~450px para
    // cima ao devolver o foco ao CTA do header preso na volta (ADR 0082).
    if (gatilho && typeof gatilho.focus === 'function') gatilho.focus({ preventScroll: true });
    gatilho = null;
});

// Esc: sai animado em vez de sumir no ato.
dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    comoFechou = 'esc';
    fechar();
});

dialog.addEventListener('click', (event) => {
    const fecho = event.target.closest('[data-fechar]');

    if (!fecho) return;

    comoFechou = fecho.classList.contains('abrir-conta__veu') ? 'overlay' : 'x';
    fechar();
});

document.addEventListener('click', (event) => {
    const origem = event.target.closest('[data-abrir-conta]');

    if (!origem) return;

    const vaiParaALoja = APARELHO && lojas !== '';

    Asa.track('account_open_click', {
        origin: origem.dataset.trackOrigin,
        outcome: vaiParaALoja ? 'store' : 'modal',
        index: origem.dataset.trackIndex,
        slide_name: origem.dataset.trackSlideName,
    });

    // No aparelho este clique JÁ é a saída para a loja: é a conversão.
    if (vaiParaALoja) Asa.track('app_store_click', { platform: 'device_redirect', origin: origem.dataset.trackOrigin });

    /*
     | Celular e tablet: direto à loja, em aba nova, como os selos do
     | `ds/download-app` — o destino é o mesmo (`redirectBasedOnDevice`, que
     | escolhe App Store ou Play Store pelo aparelho).
     |
     | `target="_blank"` já implica `noopener` nos navegadores que importam, mas
     | a feature `noopener` no `window.open` faz ele devolver `null` por
     | especificação — e aí não dá para distinguir de um pop-up barrado. Daí
     | zerar o `opener` à mão e guardar o `null` para a única leitura que resta:
     | não abriu, então navega nesta aba mesmo.
     */
    if (APARELHO && lojas) {
        event.preventDefault();

        const aba = window.open(lojas, '_blank');

        if (aba) aba.opener = null;
        else window.location.href = lojas;

        return;
    }

    if (abrir(origem)) event.preventDefault();
});

// Sem gatilho: o foco de volta é o do próprio `close` do <dialog>, e o link que
// trouxe o visitante costuma estar noutra página.
window.addEventListener('hashchange', () => {
    if (noEndereco()) abrir(null);
    else if (dialog.open) fechar();
});

if (noEndereco()) abrir(null);

});
